import { clsx } from 'clsx';
import { type ReactNode, type RefObject, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDismiss, useFocusTrap } from '../../components/ui/overlay';

const GAP = 8;
const MARGIN = 8;

type Position = { top: number; left: number };

/**
 * Where a popover goes next to a calendar entry or slot: beside it (right, else left) like Google Calendar, so
 * it never covers what it describes; below or above when neither side has room (narrow screens).
 */
function place(anchor: DOMRect, width: number, height: number): Position {
  const maxLeft = window.innerWidth - width - MARGIN;
  const maxTop = window.innerHeight - height - MARGIN;
  const sideTop = Math.max(MARGIN, Math.min(anchor.top, maxTop));
  if (anchor.right + GAP + width <= window.innerWidth - MARGIN)
    return { top: sideTop, left: anchor.right + GAP };
  if (anchor.left - GAP - width >= MARGIN) return { top: sideTop, left: anchor.left - GAP - width };
  const left = Math.max(MARGIN, Math.min(anchor.left, maxLeft));
  const below = anchor.bottom + GAP;
  return {
    top: below + height <= window.innerHeight - MARGIN ? below : Math.max(MARGIN, anchor.top - GAP - height),
    left,
  };
}

export type CalendarPopoverProps = {
  /** Element it is shown next to; null centers it. */
  anchor: HTMLElement | null;
  onClose: () => void;
  /** Accessible name of the dialog. */
  label: string;
  className?: string;
  children: ReactNode;
};

/**
 * Non-modal dialog beside a calendar entry or slot (details, quick create, a day's entries). Focus moves in
 * (`data-autofocus` first) and returns on close; Esc and a pointer-down outside close it.
 */
export function CalendarPopover({ anchor, onClose, label, className, children }: CalendarPopoverProps) {
  const ref = useRef<HTMLDivElement>(null);
  // Pressing the anchor again is not "outside": the click that follows decides (e.g. reopens the same entry).
  const anchorRef = useMemo<RefObject<HTMLElement | null>>(() => ({ current: anchor }), [anchor]);
  const [position, setPosition] = useState<Position | null>(null);
  useFocusTrap(ref, true);
  useDismiss([ref, anchorRef], true, onClose);

  useLayoutEffect(() => {
    let last: DOMRect | null = null;
    const update = () => {
      const popover = ref.current;
      if (!popover) return;
      // An anchor that re-rendered away keeps the popover where it was.
      if (anchor?.isConnected) last = anchor.getBoundingClientRect();
      const rect =
        last ??
        new DOMRect(
          window.innerWidth / 2 - popover.offsetWidth / 2,
          window.innerHeight / 3,
          popover.offsetWidth,
          0,
        );
      setPosition(place(rect, popover.offsetWidth, popover.offsetHeight));
    };
    update();
    const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(update) : null;
    if (ref.current) observer?.observe(ref.current);
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [anchor]);

  return createPortal(
    <div
      ref={ref}
      role="dialog"
      aria-label={label}
      tabIndex={-1}
      style={{ position: 'fixed', top: position?.top ?? -9999, left: position?.left ?? -9999 }}
      className={clsx(
        'z-40 max-h-[calc(100dvh-1rem)] max-w-[calc(100vw-1rem)] animate-pop-in overflow-y-auto rounded-lg border border-slate-200 bg-surface shadow-popover outline-none',
        className,
      )}
    >
      {children}
    </div>,
    document.body,
  );
}
